import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { getPremiumGalleryForColor } from '../images';
import { metaTrackViewContent, metaTrackCustomizeProduct } from '../analytics/metaPixel';
import './ProductModal.css';

function ProductModal({ product, isOpen, onClose }) {
    const { addToCart } = useCart();
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedColor, setSelectedColor] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [showMore, setShowMore] = useState(false);

    const displayImages = useMemo(() => {
        if (!product) return [];
        if (product.colors && product.colors.length > 0) {
            if (selectedColor == null) return product.images;
            return getPremiumGalleryForColor(selectedColor);
        }
        return product.images;
    }, [product, selectedColor]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setSelectedImage(0);
            setShowMore(false);
            if (product?.colors?.length) {
                setSelectedColor(null);
            } else {
                setSelectedColor(null);
            }
            if (product) {
                metaTrackViewContent(product);
            }
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, product]);

    useEffect(() => {
        setSelectedImage(0);
    }, [selectedColor, product]);

    if (!isOpen || !product) return null;

    const handleAddToCart = () => {
        const hasColors = product.colors && product.colors.length > 0;
        if (hasColors && selectedColor == null) return;
        addToCart({
            ...product,
            image: displayImages[0],
            images: displayImages,
            selectedSize: 'N/A',
            selectedColor: hasColors ? selectedColor : 'N/A',
            quantity
        });
        onClose();
    };

    const paragraphs = product.description.split('\n').map(p => p.replace('5 работни дни', '2 работни дни').replace('Безплатна доставка (2 работни дни)', '').trim()).filter(Boolean);
    const shortDesc = paragraphs[0] || '';
    const restDesc = paragraphs.slice(1);
    const showColorPicker = product.colors && product.colors.length > 0;
    const colorRequired = showColorPicker && selectedColor == null;

    return (
        <div className="product-modal-overlay" onClick={onClose}>
            <div className="product-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>x</button>
                
                <div className="modal-product-container">
                    <div className="modal-product-images">
                        <div className="modal-main-image">
                            <img src={displayImages[selectedImage]} alt={product.name} />
                        </div>
                        <div className="modal-image-thumbnails">
                            {displayImages.map((image, index) => (
                                <img
                                    key={index}
                                    src={image}
                                    alt=""
                                    className={selectedImage === index ? 'active' : ''}
                                    onClick={() => setSelectedImage(index)}
                                />
                            ))}
                        </div>
                    </div>
                    
                    <div className="modal-product-info">
                        <div className="modal-top-block">
                            <p className="modal-price">{product.price.toFixed(2)} €</p>

                            {showColorPicker && (
                                <div className="modal-color-picker" role="group" aria-label="Цвят">
                                    <span className="modal-color-label">Цвят (задължително):</span>
                                    <div className="modal-color-options">
                                        {product.colors.map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                className={
                                                    selectedColor === c
                                                        ? 'modal-color-btn modal-color-btn--active'
                                                        : 'modal-color-btn'
                                                }
                                                onClick={() => {
                                                    setSelectedColor(c);
                                                    metaTrackCustomizeProduct(product, c);
                                                }}
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="modal-buy-row">
                                <div className="quantity-control">
                                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    />
                                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddToCart}
                                    className="modal-add-to-cart-btn"
                                    disabled={colorRequired}
                                    title={colorRequired ? 'Избери цвят' : undefined}
                                >
                                    Купи
                                </button>
                            </div>
                        </div>
                        
                        <h2 className="modal-product-name">{product.name}</h2>
                        <span className="modal-urgency">Остават само 8 бр.</span>
                        
                        <div className="modal-description">
                            <p className="modal-desc-short">{shortDesc}</p>
                            {restDesc.length > 0 && (
                                <>
                                    {showMore && restDesc.map((p, i) => (
                                        <p key={i}>{p}</p>
                                    ))}
                                    <button 
                                        type="button" 
                                        className="modal-show-more"
                                        onClick={() => setShowMore(!showMore)}
                                    >
                                        {showMore ? 'По-малко' : 'Покажи повече'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductModal;
